import { PortalService } from '@seller-hermes/features/portal/portal.api';
import {
  buildLocalPortalDashboardUrl,
  setActiveSellerGroupId,
} from '@seller-hermes/features/portal/portal-local-context';
import type {
  PortalProfile,
  PortalSellerGroupContext,
} from '@seller-hermes/features/portal/portal.types';
import { Button } from '@seller-hermes/features/ui-v2/Button';
import { isStandaloneLoginRuntime } from '@seller-hermes/utils/runtime';
import { ChevronRight, Loader2 } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

const PROFILE_LABEL: Record<PortalProfile, string> = {
  LOG: 'LOG',
  GO: 'GO',
  PAY: 'PAY',
};

const getContextLabel = (context: PortalSellerGroupContext) =>
  `${PROFILE_LABEL[context.profile]}${context.is_homologation ? ' HML' : ''}`;

const getAvailableContexts = (contexts: PortalSellerGroupContext[]) =>
  contexts.filter(
    (context) => context.status !== 'unavailable' && context.profile !== 'PAY',
  );

export function LoginContextPage() {
  const navigate = useNavigate();
  const [contexts, setContexts] = React.useState<PortalSellerGroupContext[]>(
    [],
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const navigateToContext = React.useCallback(
    (context: PortalSellerGroupContext) => {
      setActiveSellerGroupId(context.id, context.profile);
      window.location.href = buildLocalPortalDashboardUrl(context);
    },
    [],
  );

  React.useEffect(() => {
    let mounted = true;

    if (!isStandaloneLoginRuntime()) {
      navigate('/', { replace: true });
      return;
    }

    PortalService.getMe()
      .then((portalMe) => {
        if (!mounted) return;

        const availableContexts = getAvailableContexts(portalMe.seller_groups);

        if (availableContexts.length === 1) {
          navigateToContext(availableContexts[0]);
          return;
        }

        if (!availableContexts.length) {
          setError('Nenhum contexto disponivel para este usuario.');
          setLoading(false);
          return;
        }

        setContexts(availableContexts);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setError(
          'Nao foi possivel carregar os contextos. Verifique se a API esta rodando.',
        );
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [navigate, navigateToContext]);

  if (loading) {
    return (
      <div className="min-h-48 flex items-center justify-center">
        <Loader2 className="text-muted-foreground animate-spin" size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 px-3 py-8 text-center">
        <p className="text-destructive max-w-sm text-sm">{error}</p>
        <Button variant="outline" onClick={() => navigate('/login')}>
          Voltar ao login
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 px-3 py-6">
      <div className="text-center">
        <p className="text-muted-foreground text-xs font-semibold uppercase">
          LOCAL
        </p>
        <h1 className="mt-1 text-lg font-semibold">Selecionar contexto</h1>
      </div>

      <div className="flex w-full max-w-md flex-col gap-2">
        {contexts.map((context) => (
          <button
            key={`${context.id}-${context.profile}-${context.is_homologation}`}
            type="button"
            onClick={() => navigateToContext(context)}
            className="border-border bg-background hover:bg-accent flex items-center justify-between rounded-md border px-4 py-3 text-left text-sm transition-colors"
          >
            <span className="font-medium">{context.name}</span>
            <span className="text-muted-foreground flex items-center gap-2">
              <span className="text-xs font-semibold">
                {getContextLabel(context)}
              </span>
              <ChevronRight size={14} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default LoginContextPage;
